import { useEffect, useRef, useState } from 'react'

import useAppStore from '$appStore'
import { logger } from '$utils'

import { INTERCOM_APP_ID, IntercomBootConfig } from './intercom'

/**
 * Boots Intercom for the signed-in user and tracks the unread message count so
 * the topbar can badge the chat launcher.
 *
 * The Intercom library itself is loaded by the host page snippet; this hook
 * configures it once the app state (user + active company) is ready, then keeps
 * it in sync if the identity or company changes. It returns the current unread
 * count (0 when there are none or Intercom is unavailable).
 */
export const useIntercom = (): { unreadCount: number } => {
  const isAppStateReady = useAppStore((state) => state.isAppStateReady)
  const userDetails = useAppStore((state) => state.userDetails)
  const activeCompany = useAppStore((state) => state.activeCompany)

  const [unreadCount, setUnreadCount] = useState(0)

  // Boot once, then `update` on later changes. The unread-count listeners are
  // also registered once - re-registering would stack duplicate callbacks.
  const bootedRef = useRef(false)
  const listenersRegisteredRef = useRef(false)

  useEffect(() => {
    const intercom = window.Intercom
    if (!intercom || !isAppStateReady || !userDetails.id) {
      return
    }

    // Hide Intercom's own floating launcher (the topbar provides the chat
    // button) but keep the custom launcher selector so legacy
    // `.intercom-launcher` links - e.g. the "contact support" links rendered in
    // AngularJS error messages - still open the messenger.
    window.intercomSettings = {
      hide_default_launcher: true,
      custom_launcher_selector: '.intercom-launcher',
      alignment: 'right',
      vertical_padding: 20,
      horizontal_padding: 20,
      app_id: INTERCOM_APP_ID,
    }

    const fullName = [userDetails.firstName, userDetails.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()

    const config: IntercomBootConfig = {
      app_id: INTERCOM_APP_ID,
      user_id: userDetails.id,
      user_hash: userDetails.intercomUserHash,
      name: fullName || undefined,
      email: userDetails.email,
      company: activeCompany?.id
        ? { company_id: activeCompany.id, name: activeCompany.name }
        : undefined,
    }

    if (!bootedRef.current) {
      logger.info('Booting Intercom for the signed-in user')
      intercom('boot', config)
      bootedRef.current = true
    } else {
      intercom('update', config)
    }

    if (!listenersRegisteredRef.current) {
      listenersRegisteredRef.current = true

      // Intercom emits a second onUnreadCountChange with 0 immediately after a
      // non-zero count (it auto-previews the new message in a minimal widget),
      // so we only act on non-zero counts and clear the badge when the user
      // actually opens the messenger (onShow).
      intercom('onUnreadCountChange', (count) => {
        if (count !== 0) {
          setUnreadCount(count)
        }
      })
      intercom('onShow', () => setUnreadCount(0))
    }
  }, [
    isAppStateReady,
    userDetails.id,
    userDetails.intercomUserHash,
    userDetails.firstName,
    userDetails.lastName,
    userDetails.email,
    activeCompany?.id,
    activeCompany?.name,
  ])

  return { unreadCount }
}
